import { keepPreviousData, useQuery } from '@tanstack/react-query'
import epsClient from 'enrollment-platform/api/eps.client'
import { ENVIROMENT_DEV, getEnvironment } from 'modules/app-config/environment'
import { useCallback, useState } from 'react'
import { TemplateType } from '../models/templateType'

export const DEFAULT_PAGE_SIZE = 50

export interface ActiveHrisMappingByType {
  type: TemplateType
  versionNumber: number
}

export interface CustomerOnboardingRow {
  clientPublicKey: string
  companyName: string
  activeHrisMappings: ActiveHrisMappingByType[]
  activeHrisMappingVersion: string | null
  activePayrollMappingVersion: string | null
  activeEligibleMappingVersion: string | null
}

interface CustomerApiResult {
  name: string
  clientPublicKey: string
  activeHrisMappings?: ActiveHrisMappingByType[]
  activeHrisMappingVersion: string | null
  activePayrollMappingVersion: string | null
  activeEligibleMappingVersion: string | null
}

interface CustomerListResponseDTO {
  data: CustomerApiResult[]
  meta: { total: number; page: number; limit: number; totalPages: number }
}

export interface CustomerOnboardingOption {
  clientPublicKey: string
  name: string
  isTestCompany: boolean
}

interface FetchParams {
  page: number
  limit: number
  clientPublicKey?: string
  includeTestCompanies: boolean
}

async function fetchCustomerOnboardingList(params: FetchParams) {
  const { data } = await epsClient.get<CustomerListResponseDTO>(
    '/api/v1/customer-onboarding',
    {
      params: {
        page: params.page,
        limit: params.limit,
        clientPublicKey: params.clientPublicKey,
        includeTestCompanies: params.includeTestCompanies,
      },
    }
  )

  const rows = data.data.map(
    (customer): CustomerOnboardingRow => ({
      clientPublicKey: customer.clientPublicKey,
      companyName: customer.name,
      activeHrisMappings: customer.activeHrisMappings ?? [],
      activeHrisMappingVersion: customer.activeHrisMappingVersion,
      activePayrollMappingVersion: customer.activePayrollMappingVersion,
      activeEligibleMappingVersion: customer.activeEligibleMappingVersion,
    })
  )

  return { rows, total: data.meta.total }
}

async function fetchCustomerOnboardingOptions(includeTestCompanies: boolean) {
  const { data } = await epsClient.get<CustomerOnboardingOption[]>(
    '/api/v1/customer-onboarding/options',
    { params: { includeTestCompanies } }
  )
  return data
}

export default function useCustomerOnboardingList() {
  const [selectedClientPublicKey, setSelectedClientPublicKey] = useState<
    string | undefined
  >()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [includeTestCompanies, setIncludeTestCompanies] = useState(
    getEnvironment() === ENVIROMENT_DEV
  )

  const { data: options = [] } = useQuery({
    queryKey: ['customerOnboardingOptions', includeTestCompanies],
    queryFn: () => fetchCustomerOnboardingOptions(includeTestCompanies),
    placeholderData: keepPreviousData,
  })

  const activeClientPublicKey = options.some(
    (option) => option.clientPublicKey === selectedClientPublicKey
  )
    ? selectedClientPublicKey
    : undefined

  const {
    data,
    isFetching: isLoading,
    error,
  } = useQuery({
    queryKey: [
      'customerOnboardingList',
      activeClientPublicKey,
      page,
      pageSize,
      includeTestCompanies,
    ],
    queryFn: () =>
      fetchCustomerOnboardingList({
        page,
        limit: pageSize,
        clientPublicKey: activeClientPublicKey,
        includeTestCompanies,
      }),
    placeholderData: keepPreviousData,
  })

  const handleSelectClient = useCallback((clientPublicKey?: string) => {
    setSelectedClientPublicKey(clientPublicKey)
    setPage(1)
  }, [])

  const handleToggleTestCompanies = useCallback((checked: boolean) => {
    setIncludeTestCompanies(checked)
    setPage(1)
  }, [])

  const handlePageChange = useCallback(
    (newPage: number, newPageSize?: number) => {
      setPage(newPage)
      if (newPageSize && newPageSize !== pageSize) {
        setPageSize(newPageSize)
        setPage(1)
      }
    },
    [pageSize]
  )

  return {
    data: data?.rows ?? [],
    total: data?.total ?? 0,
    isLoading,
    error,
    options,
    selectedClientPublicKey: activeClientPublicKey,
    page,
    pageSize,
    includeTestCompanies,
    handleToggleTestCompanies,
    handleSelectClient,
    handlePageChange,
  }
}
